import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import type { CheckinVerdict } from "@/types";

interface SaveInput {
  flagged: boolean;
  note: string;
}

export function useVerdict(bookingId: number | null) {
  const { user, profile } = useAuth();
  const [verdict, setVerdict] = useState<CheckinVerdict | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!bookingId) {
      setVerdict(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from("checkin_verdicts")
        .select("*")
        .eq("booking_id", bookingId)
        .maybeSingle();
      if (fetchError) throw fetchError;
      if (mounted.current) {
        setVerdict((data as CheckinVerdict) ?? null);
        setError(null);
      }
    } catch (err) {
      if (mounted.current) {
        setError(err instanceof Error ? err.message : "We couldn't load the damage verdict.");
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async ({ flagged, note }: SaveInput) => {
      if (!bookingId) return null;
      setSaving(true);
      setError(null);
      try {
        const { data, error: upsertError } = await supabase
          .from("checkin_verdicts")
          .upsert(
            {
              booking_id: bookingId,
              flagged,
              note: note.trim() || null,
              flagged_by: user?.id ?? null,
              flagged_by_name: profile?.full_name || user?.email || "Gari user",
              resolved: false,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "booking_id" }
          )
          .select("*")
          .single();
        if (upsertError) throw upsertError;
        if (mounted.current) setVerdict(data as CheckinVerdict);
        return data as CheckinVerdict;
      } catch (err) {
        setError(err instanceof Error ? err.message : "The verdict couldn't be saved.");
        throw err;
      } finally {
        if (mounted.current) setSaving(false);
      }
    },
    [bookingId, user, profile]
  );

  const resolve = useCallback(async () => {
    if (!bookingId) return;
    try {
      const { data } = await supabase
        .from("checkin_verdicts")
        .update({ resolved: true, updated_at: new Date().toISOString() })
        .eq("booking_id", bookingId)
        .select("*")
        .maybeSingle();
      if (data && mounted.current) setVerdict(data as CheckinVerdict);
    } catch {
      // non-critical
    }
  }, [bookingId]);

  return { verdict, loading, error, saving, save, resolve, reload: load };
}